import { useState, useCallback, useMemo } from 'react';

/**
 * Return type for the useWorkflowSelection hook.
 * Provides selection state and operations for workflow batch management.
 */
export interface UseWorkflowSelectionReturn {
  /** Set of currently selected batch IDs */
  selectedIds: Set<number>;
  /** Number of currently selected items */
  selectedCount: number;
  /** Check if a specific ID is selected */
  isSelected: (id: number) => boolean;
  /** Toggle selection state of a single ID */
  toggle: (id: number) => void;
  /** Replace selection with the provided IDs (typically all visible items) */
  selectAll: (ids: number[]) => void;
  /** Clear all selections */
  clearSelection: () => void;
  /** Add multiple IDs to the current selection */
  selectMultiple: (ids: number[]) => void;
}

/**
 * Hook for managing selection state in workflow batch lists.
 *
 * Single Responsibility: ONLY manages selection state.
 * - No data fetching
 * - No business logic
 * - No side effects
 *
 * Uses Set<number> for O(1) lookup/add/delete operations.
 * All functions are wrapped in useCallback for stable references,
 * making them safe to use as dependencies in other hooks/effects.
 *
 * @example
 * ```tsx
 * const { selectedIds, toggle, selectAll, clearSelection } = useWorkflowSelection();
 *
 * // Toggle single item
 * <Checkbox checked={isSelected(batch.id)} onChange={() => toggle(batch.id)} />
 *
 * // Select all visible
 * <Button onClick={() => selectAll(batches.map(b => b.id))}>Select All</Button>
 *
 * // Process selected
 * const handleBulkProcess = () => {
 *   processBatches([...selectedIds]);
 *   clearSelection();
 * };
 * ```
 */
export function useWorkflowSelection(): UseWorkflowSelectionReturn {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  /**
   * Check if a specific ID is currently selected.
   * Memoized based on selectedIds for stable identity when selection hasn't changed.
   */
  const isSelected = useCallback(
    (id: number): boolean => selectedIds.has(id),
    [selectedIds]
  );

  /**
   * Toggle the selection state of a single ID.
   * If selected, removes it. If not selected, adds it.
   */
  const toggle = useCallback((id: number): void => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  /**
   * Replace the entire selection with a new set of IDs.
   * Typically used for "Select All" functionality.
   */
  const selectAll = useCallback((ids: number[]): void => {
    setSelectedIds(new Set(ids));
  }, []);

  /**
   * Clear all selections.
   */
  const clearSelection = useCallback((): void => {
    setSelectedIds(new Set());
  }, []);

  /**
   * Add multiple IDs to the current selection without removing existing ones.
   * Useful for shift-click range selection or batch operations.
   */
  const selectMultiple = useCallback((ids: number[]): void => {
    setSelectedIds(prev => new Set([...prev, ...ids]));
  }, []);

  /**
   * Compute selected count from the Set size.
   * Using useMemo ensures we don't recalculate unless selectedIds changes.
   */
  const selectedCount = useMemo(() => selectedIds.size, [selectedIds]);

  return {
    selectedIds,
    selectedCount,
    isSelected,
    toggle,
    selectAll,
    clearSelection,
    selectMultiple,
  };
}
